/**
 * WrestlerDuplicates - Admin tool for finding and merging duplicate wrestlers
 *
 * Wrestlers from wrestlestat preliminary seeds sometimes have slightly different
 * names than when they come in via the trackwrestling scraper. This creates two
 * records for the same person (same tournament, team, weight class). This tool
 * lets the admin review each pair and merge them so rosters point to the correct
 * wrestler.
 */

import { useState, useEffect } from 'react';
import { adminApi } from '../../../services/admin-api';

interface DuplicateWrestler {
  id: string;
  name: string;
  seed: number | null;
  seed_source: string | null;
  place: number | null;
  team_points: number;
  status: string;
  roster_count: number;
}

interface DuplicateGroup {
  tournament_id: string;
  tournament_name: string;
  school_team: string;
  weight_class: string;
  wrestlers: DuplicateWrestler[];
}

interface MergeResult {
  rostersUpdated: number;
  rostersSkipped: number;
  membersRecalculated: number;
  removedWrestler: { id: string; name: string };
  keptWrestler: { id: string; name: string };
}

export function WrestlerDuplicates() {
  const [tournaments, setTournaments] = useState<
    { id: string; name: string; duplicate_count: number }[]
  >([]);
  const [selectedTournament, setSelectedTournament] = useState<string>('');
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [merging, setMerging] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<MergeResult | null>(null);
  const [dismissedGroups, setDismissedGroups] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTournaments();
  }, []);

  useEffect(() => {
    if (selectedTournament) {
      loadDuplicates(selectedTournament);
    } else {
      setGroups([]);
    }
  }, [selectedTournament]);

  const loadTournaments = async () => {
    try {
      setTournamentsLoading(true);
      const data = await adminApi.getTournamentsWithDuplicates();
      setTournaments(data.tournaments);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTournamentsLoading(false);
    }
  };

  const loadDuplicates = async (tournamentId: string) => {
    try {
      setLoading(true);
      setError(null);
      setDismissedGroups(new Set());
      const data = await adminApi.getDuplicateWrestlers(tournamentId);
      setGroups(data.groups);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (keepId: string, removeId: string, groupKey: string) => {
    const keepWrestler = groups.flatMap((g) => g.wrestlers).find((w) => w.id === keepId);
    const removeWrestler = groups.flatMap((g) => g.wrestlers).find((w) => w.id === removeId);

    if (
      !confirm(
        `Merge wrestlers:\n\nKEEP: "${keepWrestler?.name}" (${keepWrestler?.roster_count} rosters)\nREMOVE: "${removeWrestler?.name}" (${removeWrestler?.roster_count} rosters)\n\nAll rosters pointing to the removed wrestler will be updated to use the kept wrestler. The removed wrestler will be deleted.\n\nContinue?`
      )
    ) {
      return;
    }

    try {
      setMerging(groupKey);
      setError(null);
      const result = await adminApi.mergeWrestlers(keepId, removeId);
      setLastResult(result);

      // Remove the merged group from the list
      setGroups((prev) =>
        prev.filter(
          (g) =>
            !(
              g.tournament_id ===
                groups.find((g2) => g2.wrestlers.some((w) => w.id === keepId))?.tournament_id &&
              g.school_team ===
                groups.find((g2) => g2.wrestlers.some((w) => w.id === keepId))?.school_team &&
              g.weight_class ===
                groups.find((g2) => g2.wrestlers.some((w) => w.id === keepId))?.weight_class
            )
        )
      );

      // Simpler: just reload
      if (selectedTournament) {
        await loadDuplicates(selectedTournament);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setMerging(null);
    }
  };

  const handleDismiss = (groupKey: string) => {
    setDismissedGroups((prev) => new Set(prev).add(groupKey));
  };

  const groupKey = (g: DuplicateGroup) => `${g.tournament_id}:${g.school_team}:${g.weight_class}`;

  const visibleGroups = groups.filter((g) => !dismissedGroups.has(groupKey(g)));

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Wrestler Duplicates</h1>
        <p className="text-gray-500 mt-2">
          Find and merge duplicate wrestlers caused by name differences between wrestlestat seeds
          and trackwrestling data. Duplicates share the same tournament, team, and weight class.
        </p>
      </div>

      {/* Last merge result banner */}
      {lastResult && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-semibold text-green-800">Merge Successful</h3>
              <p className="text-sm text-green-700 mt-1">
                Kept "{lastResult.keptWrestler.name}", removed "{lastResult.removedWrestler.name}".{' '}
                {lastResult.rostersUpdated} roster(s) updated
                {lastResult.rostersSkipped > 0 &&
                  `, ${lastResult.rostersSkipped} skipped (already had kept wrestler)`}
                , {lastResult.membersRecalculated} member score(s) recalculated.
              </p>
            </div>
            <button
              onClick={() => setLastResult(null)}
              className="text-green-600 hover:text-green-800"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Tournament selector */}
      <div className="mb-6 bg-white rounded-lg shadow border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Tournament</label>
        {tournamentsLoading ? (
          <div className="animate-pulse h-10 bg-gray-200 rounded-md"></div>
        ) : tournaments.length === 0 ? (
          <p className="text-gray-500 text-sm">No tournaments with duplicate wrestlers found.</p>
        ) : (
          <select
            value={selectedTournament}
            onChange={(e) => setSelectedTournament(e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Choose a tournament --</option>
            {tournaments.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.duplicate_count} duplicate group{t.duplicate_count !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Results */}
      {!loading && selectedTournament && visibleGroups.length === 0 && groups.length === 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500">
          No duplicate wrestlers found for this tournament.
        </div>
      )}

      {!loading && visibleGroups.length === 0 && groups.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500">
          All duplicate groups have been reviewed.
        </div>
      )}

      {/* Duplicate groups */}
      {!loading && visibleGroups.length > 0 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {visibleGroups.length} duplicate group{visibleGroups.length !== 1 ? 's' : ''} found. For
            each group, click "Keep This" on the wrestler you want to keep. All rosters referencing
            the other wrestler will be updated.
          </p>

          {visibleGroups.map((group) => {
            const gk = groupKey(group);
            const isMerging = merging === gk;

            return (
              <div
                key={gk}
                className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden"
              >
                {/* Group header */}
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-gray-900">
                      {group.weight_class} lbs
                    </span>
                    <span className="mx-2 text-gray-300">|</span>
                    <span className="text-sm text-gray-600">{group.school_team}</span>
                  </div>
                  <button
                    onClick={() => handleDismiss(gk)}
                    className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-md hover:bg-gray-200"
                  >
                    Skip / Not a Duplicate
                  </button>
                </div>

                {/* Wrestlers comparison */}
                <div className="p-6">
                  <div
                    className={`grid gap-4`}
                    style={{ gridTemplateColumns: `repeat(${group.wrestlers.length}, 1fr)` }}
                  >
                    {group.wrestlers.map((wrestler) => (
                      <div
                        key={wrestler.id}
                        className="border border-gray-200 rounded-lg p-4 flex flex-col"
                      >
                        {/* Wrestler name */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          {wrestler.name}
                        </h3>

                        {/* Details */}
                        <div className="space-y-2 text-sm flex-1">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Source</span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                wrestler.seed_source === 'trackwrestling'
                                  ? 'bg-blue-100 text-blue-800'
                                  : wrestler.seed_source === 'wrestlestat'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {wrestler.seed_source || 'unknown'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Seed</span>
                            <span className="text-gray-900">{wrestler.seed ?? 'unseeded'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Place</span>
                            <span className="text-gray-900">{wrestler.place ?? '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Team Points</span>
                            <span className="text-gray-900">{wrestler.team_points}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Status</span>
                            <span
                              className={`${
                                wrestler.status === 'active' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {wrestler.status}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">On Rosters</span>
                            <span
                              className={`font-semibold ${
                                wrestler.roster_count > 0 ? 'text-orange-600' : 'text-gray-400'
                              }`}
                            >
                              {wrestler.roster_count}
                            </span>
                          </div>
                        </div>

                        {/* Keep button */}
                        <button
                          onClick={() => {
                            const others = group.wrestlers.filter((w) => w.id !== wrestler.id);
                            // For groups with >2, merge all others into this one sequentially
                            // For simplicity, merge the first "other" — admin can re-run for remaining
                            if (others.length > 0) {
                              handleMerge(wrestler.id, others[0].id, gk);
                            }
                          }}
                          disabled={isMerging}
                          className={`mt-4 w-full px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            isMerging
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {isMerging ? (
                            <span className="flex items-center justify-center">
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Merging...
                            </span>
                          ) : (
                            `Keep This (merge ${group.wrestlers.length - 1} other${group.wrestlers.length > 2 ? 's' : ''})`
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
